import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Collapse,
  Box,
} from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTheme } from "@mui/material/styles";

export type ErrorDialogProps = {
  open: boolean;
  title: string;
  message: string;
  /** Optional technical details (e.g. server response); shown in collapsible section */
  details?: string;
  onClose: () => void;
};

export default function ErrorDialog({
  open,
  title,
  message,
  details,
  onClose,
}: ErrorDialogProps) {
  const theme = useTheme();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleCopyDetails = () => {
    const text = details ? `${message}\n\nDetails:\n${details}` : message;
    navigator.clipboard.writeText(text);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          borderTop: "4px solid",
          borderTopColor: theme.palette.error.main,
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700, pb: 0 }}>
        <ErrorOutlineOutlinedIcon color="error" sx={{ fontSize: 28 }} />
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Typography variant="body1" color="text.primary">
          {message}
        </Typography>
        {details != null && details !== "" && (
          <>
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                startIcon={detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setDetailsOpen((o) => !o)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {detailsOpen ? "Hide details" : "Show details"}
              </Button>
            </Box>
            <Collapse in={detailsOpen}>
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  fontSize: "0.8rem",
                  overflow: "auto",
                  maxHeight: 200,
                }}
              >
                {details}
              </Box>
            </Collapse>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        {details != null && details !== "" && (
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyDetails}
            sx={{ mr: "auto" }}
          >
            Copy details
          </Button>
        )}
        <Button variant="contained" color="primary" onClick={onClose}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
